import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useRouter } from "next/router";
import { Button, CardBody, Spinner, UncontrolledTooltip } from "reactstrap";
import * as yup from "yup";

import PackagingItems from "./packaging-items";
import { defaultShippingItems } from "@/utils/constants";
import useAlertBar from "@/hooks/useAlertBar";
import { routes } from "@/utils/routes";
import classes from "@/styles/get-quotes/get-quotes-main.module.css";

type Props = {
  formSubmit: (data: any) => void;
  hideServices: () => void;
  loading: boolean;
  blockRates: boolean;
  shipFromCountry: string;
  shipToCountry: string;
};

const itemSchema = yup.object().shape({
  length: yup
    .number()
    .typeError("Length must be a number")
    .positive("Length must be a positive number")
    .required("Length is required"),
  quantity: yup
    .number()
    .typeError("Quantity must be a number")
    .positive("Length must be a positive number")
    .required("Quantity is required"),
  width: yup
    .number()
    .typeError("Width must be a number")
    .positive("Width must be a positive number")
    .required("Width is required"),
  height: yup
    .number()
    .typeError("Height must be a number")
    .positive("Height must be a positive number")
    .required("Height is required"),
  weight: yup
    .number()
    .typeError("Weight must be a number")
    .positive("Weight must be a positive number")
    .required("Weight is required"),
});

const PackageItemsForm = ({
  formSubmit,
  loading,
  shipFromCountry,
  shipToCountry,
  hideServices,
  blockRates,
}: Props) => {
  const { openAlertBar } = useAlertBar();
  const router = useRouter();
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      shippingItems: defaultShippingItems,
      unitOfMeasurement: "METRIC",
    },
  });

  useEffect(() => {
    hideServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("shippingItems"), watch("unitOfMeasurement")]);

  const onSubmit = async (data: any) => {
    try {
      const valSchema = yup.array().of(itemSchema);
      await valSchema.validate(data.shippingItems, {
        abortEarly: false,
      });

      formSubmit(data);
    } catch (error: any) {
      openAlertBar(
        <ul>
          {error.errors.map((e: string, i: number) => (
            <li key={i}>{e}</li>
          ))}
        </ul>,
        false
      );
    }
  };

  return (
    <CardBody>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <PackagingItems
          reset={reset}
          watch={watch}
          control={control}
          register={register}
          setValue={setValue}
        />
        {blockRates ? (
          <div
            className={`d-flex justify-content-center gap-3 align-items-center mt-5`}
          >
            <div className="text-green">
              You have reached request limit,
              <strong> Please Sign Up to continue</strong>.
            </div>
            <Button
              className="btn-darkBlue"
              color="primary"
              onClick={() => router.push(routes.register)}
              style={{ color: "#fff" }}
            >
              SIGN UP NOW FOR FREE
            </Button>
          </div>
        ) : (
          <Button
            className={`btn-darkBlue text-uppercase fw-bold ${classes.btnSubmitFloat}`}
            type="submit"
            disabled={loading}
            id="getPackagingRatesBtn"
          >
            Get Rates
            {loading && <Spinner size="sm" className="ms-3" />}
          </Button>
        )}
        {shipToCountry !== shipFromCountry && (
          <UncontrolledTooltip target="getPackagingRatesBtn">
            For international shipments, you need to provide the declared value
            of items to get UPS rates.
          </UncontrolledTooltip>
        )}
      </form>
    </CardBody>
  );
};

export default PackageItemsForm;
